import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Dialog,
  Heading,
  Badge,
  Table,
  Card,
} from '@radix-ui/themes';
import {
  Cross2Icon,
  UploadIcon,
  DownloadIcon,
  CopyIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { scheduleApi } from '../services/api';

interface Appointment {
  time: string | null;
  patient_name: string;
  date_of_birth: string | null;
  provider: string | null;
  visit_type: string | null;
  ccm_status: 'Active' | 'Pending Onboarding' | 'Not in System';
  matched_patient_id: string | null;
  diseases: string[];
}

interface ScanResult {
  appointments: Appointment[];
  summary: {
    total: number;
    active: number;
    pending: number;
    not_in_system: number;
  };
}

type DialogState = 'idle' | 'scanning' | 'results' | 'error';

interface ScheduleScanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

function getStatusBadge(status: string) {
  switch (status) {
    case 'Active':
      return <Badge color="green">Active</Badge>;
    case 'Pending Onboarding':
      return <Badge color="yellow">Pending Onboarding</Badge>;
    default:
      return <Badge color="gray">Not in System</Badge>;
  }
}

export default function ScheduleScanDialog({ open, onOpenChange }: ScheduleScanDialogProps) {
  const [state, setState] = useState<DialogState>('idle');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setState('idle');
    setResult(null);
    setErrorMsg('');
    setIsDragOver(false);
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [onOpenChange, reset]
  );

  const processFile = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setErrorMsg('Unsupported file type. Please upload a PDF, JPG, or PNG.');
      setState('error');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrorMsg('File is too large. Maximum size is 20 MB.');
      setState('error');
      return;
    }

    setState('scanning');

    try {
      const base64 = await fileToBase64(file);
      const data = await scheduleApi.scanSchedule(base64, file.type);
      setResult(data);
      setState('results');
    } catch (err: any) {
      const msg =
        err?.errorData?.error ||
        err?.errorData?.details ||
        err?.message ||
        'Failed to scan schedule. Please try again.';
      setErrorMsg(msg);
      setState('error');
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      // Reset so re-selecting the same file fires onChange again
      e.target.value = '';
    },
    [processFile]
  );

  // --- CSV / Clipboard ---

  const buildCSVContent = useCallback(() => {
    if (!result) return '';
    const headers = ['Time', 'Patient Name', 'DOB', 'Provider', 'Visit Type', 'CCM Status', 'Diseases'];
    const rows = result.appointments.map((a) => [
      a.time || '',
      a.patient_name,
      a.date_of_birth || '',
      a.provider || '',
      a.visit_type || '',
      a.ccm_status,
      a.diseases.join('; '),
    ]);
    const escape = (v: string) => (v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v);
    return [headers.join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
  }, [result]);

  const handleDownloadCSV = useCallback(() => {
    const csv = buildCSVContent();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schedule-scan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  }, [buildCSVContent]);

  const handleCopyClipboard = useCallback(async () => {
    if (!result) return;
    const headers = ['Time', 'Patient Name', 'DOB', 'Provider', 'Visit Type', 'CCM Status', 'Diseases'];
    const rows = result.appointments.map((a) =>
      [a.time || '', a.patient_name, a.date_of_birth || '', a.provider || '', a.visit_type || '', a.ccm_status, a.diseases.join('; ')].join('\t')
    );
    const text = [headers.join('\t'), ...rows].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [result]);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content style={{ maxWidth: 900, maxHeight: '85vh', overflow: 'auto' }}>
        <Flex justify="between" align="center" mb="4">
          <Dialog.Title asChild>
            <Heading size="5">Scan Appointment Schedule</Heading>
          </Dialog.Title>
          <Dialog.Close>
            <Button variant="ghost" color="gray" size="1">
              <Cross2Icon />
            </Button>
          </Dialog.Close>
        </Flex>

        {/* Idle — file drop zone */}
        {state === 'idle' && (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragOver ? '#3b82f6' : '#cbd5e1'}`,
              borderRadius: 12,
              padding: '48px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver ? '#eff6ff' : '#f8fafc',
              transition: 'all 0.15s',
            }}
          >
            <UploadIcon width="32" height="32" style={{ margin: '0 auto 12px', color: '#64748b' }} />
            <Text size="3" weight="medium" style={{ display: 'block', color: '#334155' }}>
              Drag & drop or click to browse
            </Text>
            <Text size="2" style={{ display: 'block', marginTop: 4, color: '#94a3b8' }}>
              Accepts PDF, JPG, or PNG (max 20 MB)
            </Text>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        )}

        {/* Scanning */}
        {state === 'scanning' && (
          <Box style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-4" />
            <Text size="3" style={{ color: '#475569' }}>
              Analyzing schedule...
            </Text>
          </Box>
        )}

        {/* Error */}
        {state === 'error' && (
          <Box style={{ textAlign: 'center', padding: '36px 24px' }}>
            <Text size="3" color="red" style={{ display: 'block', marginBottom: 16 }}>
              {errorMsg}
            </Text>
            <Button variant="outline" onClick={reset}>
              <ReloadIcon width="16" height="16" /> Try Again
            </Button>
          </Box>
        )}

        {/* Results */}
        {state === 'results' && result && (
          <Box>
            {/* Summary cards */}
            <Flex gap="3" mb="4" wrap="wrap">
              <SummaryCard label="Total" value={result.summary.total} color="#3b82f6" />
              <SummaryCard label="Active" value={result.summary.active} color="#22c55e" />
              <SummaryCard label="Pending Onboarding" value={result.summary.pending} color="#eab308" />
              <SummaryCard label="Not in System" value={result.summary.not_in_system} color="#94a3b8" />
            </Flex>

            {/* Results table */}
            <Box style={{ overflowX: 'auto', maxHeight: '45vh', overflowY: 'auto' }}>
              <Table.Root size="1" variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Patient Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>DOB</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Provider</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Visit Type</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>CCM Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Diseases</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {result.appointments.map((appt, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{appt.time || '-'}</Table.Cell>
                      <Table.Cell style={{ fontWeight: 500 }}>{appt.patient_name}</Table.Cell>
                      <Table.Cell>{appt.date_of_birth || '-'}</Table.Cell>
                      <Table.Cell>{appt.provider || '-'}</Table.Cell>
                      <Table.Cell>{appt.visit_type || '-'}</Table.Cell>
                      <Table.Cell>{getStatusBadge(appt.ccm_status)}</Table.Cell>
                      <Table.Cell>
                        {appt.diseases.length > 0 ? appt.diseases.join(', ') : '-'}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Action buttons */}
            <Flex gap="2" mt="4" justify="end">
              <Button variant="outline" onClick={reset}>
                <UploadIcon width="16" height="16" /> Scan Another
              </Button>
              <Button variant="outline" onClick={handleCopyClipboard}>
                <CopyIcon width="16" height="16" /> Copy to Clipboard
              </Button>
              <Button variant="solid" onClick={handleDownloadCSV}>
                <DownloadIcon width="16" height="16" /> Download CSV
              </Button>
            </Flex>
          </Box>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

// --- Helpers ---

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Card style={{ flex: '1 1 140px', minWidth: 140 }}>
      <Flex direction="column" align="center" gap="1" p="2">
        <Text size="5" weight="bold" style={{ color }}>
          {value}
        </Text>
        <Text size="1" style={{ color: '#64748b' }}>
          {label}
        </Text>
      </Flex>
    </Card>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the data:...;base64, prefix
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
